import React, { useState, useCallback, useEffect } from 'react';
import { Skeleton, Empty, Divider } from 'antd';
import { GlobalOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../../redux/store';
import { fetchDomains, addDomain, removeDomain } from '../store/settingsSlice';
import {
  SettingsCard,
  CardHeader,
  CardTitle,
  CardTitleText,
  SaveChangesButton,
  DomainSkeletonList,
  EmptyDescription,
} from '../Styles';
import { CARD_ICON_STYLE } from '../constants';
import { notification } from '../../../commonComponents/Notification/Notification';
import type { DomainSectionProps } from '../types';
import DomainCard from './DomainCard';
import AddDomainForm from './AddDomainForm';

const DomainSection: React.FC<DomainSectionProps> = ({ profileLoading }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { domains, loading } = useSelector((state: RootState) => state.settings);

  const [showAddForm, setShowAddForm] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    dispatch(fetchDomains());
  }, [dispatch]);

  // A merchant can only add when they have no domain or their existing one is in error state
  const existingDomain = domains[0];
  const canAddDomain = !existingDomain || existingDomain.status === 'error';

  const handleAdd = useCallback(
    async (data: { domain: string; domainType: 'root_domain' | 'subdomain' }) => {
      try {
        await dispatch(addDomain(data)).unwrap();
        notification.success('Domain Submitted', `${data.domain} has been submitted for approval.`);
        setShowAddForm(false);
      } catch (error: any) {
        const message = typeof error === 'string' ? error : error?.message || 'Failed to add domain.';
        notification.error(message);
      }
    },
    [dispatch]
  );

  const handleRemove = useCallback(async () => {
    setRemoving(true);
    try {
      await dispatch(removeDomain()).unwrap();
      notification.success('Domain Removed', 'The domain has been unlinked.');
    } catch (error: any) {
      const message = typeof error === 'string' ? error : error?.message || 'Failed to remove domain.';
      notification.error(message);
    } finally {
      setRemoving(false);
    }
  }, [dispatch]);

  return (
    <SettingsCard>
      <CardHeader>
        <CardTitle>
          <GlobalOutlined style={CARD_ICON_STYLE} />
          <CardTitleText level={4}>Custom Domain</CardTitleText>
        </CardTitle>
        {!showAddForm && canAddDomain && (
          <SaveChangesButton
            icon={<PlusOutlined />}
            onClick={() => setShowAddForm(true)}
            disabled={profileLoading || loading.domains}
          >
            Add Domain
          </SaveChangesButton>
        )}
      </CardHeader>

      {loading.domains ? (
        <DomainSkeletonList>
          {[1, 2].map((i) => (
            <Skeleton.Input key={i} active style={{ width: '100%', height: 60, borderRadius: 8 }} />
          ))}
        </DomainSkeletonList>
      ) : domains.length === 0 && !showAddForm ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <EmptyDescription>
              No domain added yet. Click <strong>Add Domain</strong> to get started.
            </EmptyDescription>
          }
        />
      ) : (
        <div>
          {domains.map((d) => (
            <DomainCard
              key={d.id}
              domain={d}
              onRemove={handleRemove}
              removing={removing}
            />
          ))}
        </div>
      )}

      {showAddForm && (
        <>
          {domains.length > 0 && <Divider style={{ margin: '16px 0' }} />}
          <AddDomainForm
            loading={loading.addDomain}
            onSave={handleAdd}
            onCancel={() => setShowAddForm(false)}
          />
        </>
      )}
    </SettingsCard>
  );
};

export default React.memo(DomainSection);
